/**
 * Router central — TanStack Router en modo code-based.
 *
 * Árbol:
 *   /                                                  → HomeView (login + workspace dashboard)
 *   /login                                             → redirect a `/` (compat de links externos)
 *   /templates                                         → TemplatesView (lista + outlet)
 *   /templates/$templateId                             → TemplateDetailView
 *   /templates/methodologies/$methodologyId            → MethodologyDetailView
 *   /projects                                          → ProjectsListView
 *   /projects/$projectId                               → ProjectShell (sub-layout)
 *     /projects/$projectId/overview                    → ProjectOverviewView (default)
 *     /projects/$projectId/lifecycle                   → ProjectLifecycleView
 *     /projects/$projectId/team                        → ProjectTeamView
 *     /projects/$projectId/official                    → ProjectOfficialView
 *     /projects/$projectId/official/$artifactId        → ProjectOfficialArtifactView
 *     /projects/$projectId/change-requests             → ProjectChangeRequestsView (placeholder Fase E)
 *   /approvals/$artifactId                             → ApprovalView (OT-0013 — hermana, no hija, del shell)
 *
 * Routes apuntan a componentes (no archivos). La ruta `/projects/$id`
 * sin sufijo redirecciona implícitamente al overview por convención del
 * shell — TanStack no soporta redirect declarativo en code-based, así
 * que ProjectShell renderea Outlet vacío y el item de tab "Overview"
 * está activo por defecto cuando hay match en la ruta padre sin hijo.
 */

import {
  createRootRoute,
  createRoute,
  createRouter,
  redirect,
} from '@tanstack/react-router'

import { RootLayout } from '../routes/RootLayout'
import { HomeView } from '../routes/HomeView'
import { NotFoundView } from '../routes/NotFoundView'
import {
  MethodologyDetailRoute,
  TemplateDetailRoute,
  TemplatesRoute,
  TenantSourcesRoute,
  TenantTeamRoute,
} from '../routes/tenantInternalRoutes'
import {
  ConnectedAgentsRoute,
  PlatformTenantDetailRoute,
  PlatformTenantsRoute,
  PlatformUsageRoute,
  ProjectShellRoute,
  ProjectsListRoute,
  TenantUsageRoute,
} from '../routes/gatedRoutes'
import { ProjectOverviewView } from '../routes/projects/ProjectOverviewView'
import { ProjectLifecycleView } from '../routes/projects/ProjectLifecycleView'
import { ProjectTeamView } from '../routes/projects/ProjectTeamView'
import { ProjectOfficialView } from '../routes/projects/ProjectOfficialView'
import { ProjectOfficialArtifactView } from '../routes/projects/ProjectOfficialArtifactView'
import { ProjectOfficialArtifactEditView } from '../routes/projects/ProjectOfficialArtifactEditView'
import { ProjectOfficialArtifactPreviewView } from '../routes/projects/ProjectOfficialArtifactPreviewView'
import { ProjectChangeRequestsView } from '../routes/projects/ProjectChangeRequestsView'
import { ApprovalView } from '../routes/approvals/ApprovalView'
import { ProjectSourcesView } from '../routes/projects/ProjectSourcesView'
import { ProjectUsageView } from '../routes/projects/ProjectUsageView'
import { NotificationsView } from '../routes/settings/NotificationsView'
import { ConnectAgentView } from '../routes/ConnectAgentView'

const rootRoute = createRootRoute({
  component: RootLayout,
})

const indexRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/',
  component: HomeView,
})

// ─── /login ────────────────────────────────────────────────────────────────
// La app no tiene pantalla de login propia: `HomeView` en `/` renderiza
// el panel de Entra cuando no hay sesión. `/login` existe solo como
// alias estable para links que viven fuera del bundle y no se pueden
// reescribir — el CTA "Aceptar invitación" de los correos transaccionales
// (`EmailNotificationService._login_url`) apunta aquí. Sin esta ruta, esos
// correos aterrizan en el 404 del router.
const loginRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/login',
  beforeLoad: () => {
    // `replace` evita que "atrás" devuelva al alias y rebote otra vez.
    throw redirect({ to: '/', replace: true })
  },
})

// ─── /templates ────────────────────────────────────────────────────────────
// Catálogo y detail son hermanos bajo el root: el catálogo es full-page
// (no two-column con outlet) desde B.6.d.1.
// Search params: persisten tab/typeFilter/search/sortBy en URL para que
// "Volver al catálogo" preserve el contexto y los filtros sean
// shareables vía link. Validación defensiva: cualquier valor inválido
// cae al default (undefined ⇒ initial state del componente).
// `methodologies` muestra la lista de metodologías en vez de plantillas
// (OT-0055). El tipo `methodology` no se filtra: es el "modo de
// ejecución" reservado (Doc 06), sin plantillas.
const VALID_TEMPLATES_TABS = ['all', 'system', 'custom', 'methodologies'] as const
const VALID_TEMPLATES_TYPES = [
  'lifecycle',
  'artifact',
  'approval_workflow',
  'role',
] as const
const VALID_TEMPLATES_SORTS = [
  'name_asc',
  'name_desc',
  'updated_desc',
  'created_desc',
  'created_asc',
] as const

export type TemplatesTabParam = (typeof VALID_TEMPLATES_TABS)[number]
export type TemplatesTypeParam = (typeof VALID_TEMPLATES_TYPES)[number]
export type TemplatesSortParam = (typeof VALID_TEMPLATES_SORTS)[number]

/** Filtro por metodología: `generic` (sin metodología) o el id de una. */
export const GENERIC_METHODOLOGY_FILTER = 'generic'

export interface TemplatesSearch {
  tab?: TemplatesTabParam
  type?: TemplatesTypeParam
  q?: string
  sort?: TemplatesSortParam
  methodology?: string
}

const templatesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/templates',
  component: TemplatesRoute,
  validateSearch: (search: Record<string, unknown>): TemplatesSearch => {
    const tab =
      typeof search.tab === 'string' &&
      (VALID_TEMPLATES_TABS as readonly string[]).includes(search.tab)
        ? (search.tab as TemplatesTabParam)
        : undefined
    const type =
      typeof search.type === 'string' &&
      (VALID_TEMPLATES_TYPES as readonly string[]).includes(search.type)
        ? (search.type as TemplatesTypeParam)
        : undefined
    const q =
      typeof search.q === 'string' && search.q.trim() !== ''
        ? search.q
        : undefined
    const sort =
      typeof search.sort === 'string' &&
      (VALID_TEMPLATES_SORTS as readonly string[]).includes(search.sort)
        ? (search.sort as TemplatesSortParam)
        : undefined
    const methodology =
      typeof search.methodology === 'string' && search.methodology.trim() !== ''
        ? search.methodology
        : undefined
    return {
      ...(tab !== undefined ? { tab } : {}),
      ...(type !== undefined ? { type } : {}),
      ...(q !== undefined ? { q } : {}),
      ...(sort !== undefined ? { sort } : {}),
      ...(methodology !== undefined ? { methodology } : {}),
    }
  },
})

const templateDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/templates/$templateId',
  component: TemplateDetailRoute,
})

const methodologyDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/templates/methodologies/$methodologyId',
  component: MethodologyDetailRoute,
})

// ─── /projects ─────────────────────────────────────────────────────────────
// Search params persisten filtros status/methodology cuando el usuario
// navega al detalle de un proyecto y regresa.
const VALID_PROJECT_STATUSES = [
  'draft',
  'active',
  'on_hold',
  'closed',
  'cancelled',
] as const
const VALID_PROJECT_METHODOLOGIES = [
  'waterfall',
  'scrum',
  'kanban',
  'hybrid',
] as const

export type ProjectsListStatusParam =
  (typeof VALID_PROJECT_STATUSES)[number]
export type ProjectsListMethodologyParam =
  (typeof VALID_PROJECT_METHODOLOGIES)[number]

export interface ProjectsListSearch {
  status?: ProjectsListStatusParam
  methodology?: ProjectsListMethodologyParam
}

const projectsListRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects',
  component: ProjectsListRoute,
  validateSearch: (search: Record<string, unknown>): ProjectsListSearch => {
    const status =
      typeof search.status === 'string' &&
      (VALID_PROJECT_STATUSES as readonly string[]).includes(search.status)
        ? (search.status as ProjectsListStatusParam)
        : undefined
    const methodology =
      typeof search.methodology === 'string' &&
      (VALID_PROJECT_METHODOLOGIES as readonly string[]).includes(
        search.methodology,
      )
        ? (search.methodology as ProjectsListMethodologyParam)
        : undefined
    return {
      ...(status !== undefined ? { status } : {}),
      ...(methodology !== undefined ? { methodology } : {}),
    }
  },
})

const projectShellRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/projects/$projectId',
  // Gate por tier antes de `ProjectShell` (OT-0017): el `external` va a
  // `/approvals/$artifactId` si la URL nombra un artefacto, o a "sin acceso".
  component: ProjectShellRoute,
  // Si el user navega a /projects/$id sin sufijo, redirect al overview.
  beforeLoad: ({ params, location }) => {
    if (location.pathname === `/projects/${params.projectId}`) {
      throw redirect({
        to: '/projects/$projectId/overview',
        params: { projectId: params.projectId },
      })
    }
  },
})

const projectOverviewRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: 'overview',
  component: ProjectOverviewView,
})

const projectLifecycleRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: 'lifecycle',
  component: ProjectLifecycleView,
})

const projectTeamRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: 'team',
  component: ProjectTeamView,
})

// Search params del listado de documentos oficiales — persisten search
// (`q`) y toggle `superseded` cuando el usuario navega al detalle de un
// artifact y regresa. Mismo patrón que el catálogo de plantillas y la
// lista de proyectos.
export interface ProjectOfficialSearch {
  q?: string
  superseded?: boolean
}
const projectOfficialRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: 'official',
  component: ProjectOfficialView,
  validateSearch: (search: Record<string, unknown>): ProjectOfficialSearch => {
    const q =
      typeof search.q === 'string' && search.q.trim() !== ''
        ? search.q
        : undefined
    const superseded = search.superseded === true || search.superseded === 'true'
    return {
      ...(q !== undefined ? { q } : {}),
      ...(superseded ? { superseded: true } : {}),
    }
  },
})

const projectOfficialArtifactRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: 'official/$artifactId',
  component: ProjectOfficialArtifactView,
})

const projectOfficialArtifactEditRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: 'official/$artifactId/edit',
  component: ProjectOfficialArtifactEditView,
  // Doc 07 — `?v=<version_id>` opcional: cuando el user abre una
  // versión draft v(N+1) post-canon, el NewVersionDialog navega aquí
  // con la version_id de la nueva draft. El EditView usa ese id en
  // lugar de `artifact.current_version_id` (que sigue apuntando al
  // canon vigente para no afectar la vista de oficial del proyecto).
  validateSearch: (search: Record<string, unknown>) => ({
    v: typeof search.v === 'string' ? search.v : undefined,
  }),
})

// ─── /projects/$projectId/official/$artifactId/preview ───────────────────
// Vista previa del draft. Llega con click en "Vista previa" desde el
// editor. Read-only; el banner advierte que el doc aún no es official.
const projectOfficialArtifactPreviewRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: 'official/$artifactId/preview',
  component: ProjectOfficialArtifactPreviewView,
})

const projectChangeRequestsRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: 'change-requests',
  component: ProjectChangeRequestsView,
})

// ─── /projects/$projectId/sources ──────────────────────────────────────────
// Tab "Fuentes" — insumos del proyecto (correos, minutas, transcripciones,
// PDFs externos, normativas). NO son official. Sprint A: solo storage + listado.
// Integración con AI agents llega en Sprint C (Doc 06 §"RAG híbrido").
const projectSourcesRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: 'sources',
  component: ProjectSourcesView,
})

// ─── /projects/$projectId/usage ────────────────────────────────────────────
// Tab "Consumo IA" — reporte de tokens y costos scoped al proyecto.
// Doc 25 §F3.
const projectUsageRoute = createRoute({
  getParentRoute: () => projectShellRoute,
  path: 'usage',
  component: ProjectUsageView,
})

// ─── /approvals/$artifactId ─────────────────────────────────────────────────
// OT-0013 — HERMANA de `projectShellRoute`, no hija: no llama `getProject`.
// Existe para que un aprobador `external` (a quien `can_view_project`
// niega por diseño, Doc 29 §4.6) pueda abrir su pendiente desde la
// bandeja. Miembros y admins siguen yendo a
// `/projects/$projectId/official/$artifactId` — `HomeView.tsx` decide el
// destino del link con `isExternal(role)`.
const approvalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/approvals/$artifactId',
  component: ApprovalView,
})

// ─── /settings/knowledge-base ──────────────────────────────────────────────
// Knowledge base del tenant — sources que aplican a TODOS los proyectos
// del tenant. Ej: normativa ISO, stack aprobado, lessons learned. Sprint A.
// La leen los tiers internos (gate de OT-0015); subir y borrar es de admin.
const settingsTenantSourcesRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/knowledge-base',
  component: TenantSourcesRoute,
})

// ─── /settings/team ────────────────────────────────────────────────────────
// Gestión del equipo del tenant: invitar/revocar usuarios y ver miembros
// activos. Solo `tenant_admin` puede operar; los demás tiers internos la
// ven en read-only y el `external` no la alcanza (OT-0015, `TierGate`).
// Doc 12 (Identity) — modelo de roles post-migración 0033.
const settingsTenantTeamRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/team',
  component: TenantTeamRoute,
})

// ─── /settings/usage ───────────────────────────────────────────────────────
// Dashboard de consumo de IA del tenant. Admin ve totales del tenant +
// saldo + threshold; usuarios normales ven solo lo suyo (server-enforced).
// El `external` no la alcanza (Doc 29 §2.1, OT-0017, `TierGate`).
// Doc 25 §F3.
const settingsTenantUsageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/usage',
  component: TenantUsageRoute,
})

// ─── /platform/usage ───────────────────────────────────────────────────────
// Pantalla cross-tenant reservada a platform admins (Doc 25 §F4).
// Tres tabs: Global, Por tenant, Margen. Backend rechaza con 403 a
// usuarios sin is_platform_admin; la ruta ni se monta para ellos
// (`TierGate`, OT-0017).
const platformUsageRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform/usage',
  component: PlatformUsageRoute,
})

// ─── /platform/clients ─────────────────────────────────────────────────────
// Consola de clientes (OT-0073): lista con alta y detalle con historial y
// acciones de ciclo de vida. Mismo gate que /platform/usage.
const platformClientsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform/clients',
  component: PlatformTenantsRoute,
})

const platformClientDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/platform/clients/$tenantId',
  component: PlatformTenantDetailRoute,
})

// ─── /settings/notifications ───────────────────────────────────────────────
// Preferencias de notificación del usuario. Por cada event_type
// soportado, el usuario decide qué canales recibir (in_app, email).
// Doc 21 — Fase Notif-1.e.
const settingsNotificationsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/notifications',
  component: NotificationsView,
})

// ─── /settings/agents ──────────────────────────────────────────────────────
// Agentes de IA conectados por MCP (OT-0043, Docs/15 §3.4): listar y
// desconectar. Un admin ve también los de toda la organización. El
// `external` no la alcanza (`canUseAgentAccess`, OT-0017).
const settingsConnectedAgentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/settings/agents',
  component: ConnectedAgentsRoute,
})

// ─── /connect-agent?request=<id> ───────────────────────────────────────────
// Pantalla de consentimiento a la que el servidor de autorización del MCP
// manda el navegador (OT-0043, Docs/15 §3.1). Layout enfocado: el usuario
// llega desde Claude/ChatGPT a decidir una sola cosa.
const connectAgentRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: '/connect-agent',
  component: ConnectAgentView,
  staticData: { layout: 'focused' },
  validateSearch: (search: Record<string, unknown>) => ({
    ...(typeof search.request === 'string' ? { request: search.request } : {}),
  }),
})

// Exportado para tests: permite montar el árbol con una history en
// memoria sin arrastrar la instancia global del router.
export const routeTree = rootRoute.addChildren([
  indexRoute,
  loginRoute,
  templatesRoute,
  templateDetailRoute,
  methodologyDetailRoute,
  projectsListRoute,
  projectShellRoute.addChildren([
    projectOverviewRoute,
    projectLifecycleRoute,
    projectTeamRoute,
    projectOfficialRoute,
    projectOfficialArtifactRoute,
    projectOfficialArtifactEditRoute,
    projectOfficialArtifactPreviewRoute,
    projectChangeRequestsRoute,
    projectSourcesRoute,
    projectUsageRoute,
  ]),
  approvalRoute,
  settingsTenantSourcesRoute,
  settingsTenantTeamRoute,
  settingsTenantUsageRoute,
  settingsNotificationsRoute,
  settingsConnectedAgentsRoute,
  connectAgentRoute,
  platformUsageRoute,
  platformClientsRoute,
  platformClientDetailRoute,
])

export const router = createRouter({
  routeTree,
  defaultPreload: 'intent',
  // Sin esto, cualquier URL sin match cae al fallback de TanStack: el
  // texto crudo "Not Found", sin marca ni salida hacia la app.
  defaultNotFoundComponent: NotFoundView,
})

// Type augmentation: registra el router en el modulo global para que
// useParams/useRouterState/Link tengan tipos completos.
declare module '@tanstack/react-router' {
  interface Register {
    router: typeof router
  }
  interface StaticDataRouteOption {
    /** `focused`: la ruta se pinta sin sidebar ni topbar (ver `RootLayout`). */
    layout?: 'focused'
  }
}
